import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { FormGroup, ValidationErrors } from '@angular/forms';
import { DateTime } from 'luxon';
import { IncidentFormControls, IncidentFormError } from '../../../../../types';

@Component({
  selector: 'app-review-form-incident-date',
  templateUrl: './review-form-incident-date.component.html',
  styleUrl: './review-form-incident-date.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ReviewIncidentDateComponent {
  public form = input.required<FormGroup<IncidentFormControls>>();
  public minIncidentDate = input<Date | undefined>(undefined);
  public maxIncidentDate = input<Date | undefined>(undefined);
  public noBoundaries = input<boolean>(false);
  public readonly = input<boolean>(false);
  public displayTimezone = input<boolean>(false);
  public timezone = input<string | undefined>(undefined);

  public minDate = computed(() => {
    if (this.noBoundaries()) return undefined;
    return this.minIncidentDate() ?? DateTime.now().minus({ weeks: 2 }).toJSDate();
  });

  public maxDate = computed(() => {
    if (this.noBoundaries()) return undefined;
    return this.maxIncidentDate() ?? DateTime.now().toJSDate();
  });

  public onDateTimeChange(date: string | Date) {
    if (this.readonly()) return;
    if (!date) return;
    if (typeof date === 'string') return;
    this.form().controls.instance_at.markAsDirty();
    this.form().controls.instance_at.setValue(date);
    this.form().controls.instance_at.markAsTouched();
    this.form().controls.instance_at.setErrors(this.validateDateRange(date));
    this.form().updateValueAndValidity();
  }

  public onDateChange(date: string | Date) {
    if (this.readonly()) return;
    if (!(date instanceof Date)) return;
    const current = this.form().controls.instance_at.value;
    const updated = new Date(date);
    if (current instanceof Date) updated.setHours(current.getHours(), current.getMinutes(), 0, 0);
    this.form().controls.instance_at.setValue(updated);
    this.form().controls.instance_at.markAsTouched();
    this.form().controls.instance_at.setErrors(this.validateDateRange(updated));
  }

  public onInvalidDate() {
    if (this.readonly()) return;
    this.form().controls.instance_at.markAsTouched();
    this.form().controls.instance_at.setErrors({ [IncidentFormError.INSTANCE_AT_INVALID]: true });
  }

  private validateDateRange(date: Date): ValidationErrors | null {
    const value = DateTime.fromJSDate(date).startOf('day');
    const min = this.minDate() ? DateTime.fromJSDate(this.minDate()).startOf('day') : null;
    const max = this.maxDate() ? DateTime.fromJSDate(this.maxDate()).startOf('day') : null;
    if ((min && value < min) || (max && value > max)) {
      return { [IncidentFormError.INSTANCE_AT_OUT_OF_RANGE]: true };
    }
    return null;
  }
}
